/* src/features/5DD/AudioRecorder.js */
import React, { useState, useRef, useEffect } from 'react';
import { storage, db, functions } from '../../apiClient';
import { audioDb } from '../../services/audioStorage';

const styles = {
  container: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '30px', borderRadius: '12px', backgroundColor: 'rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.1)', marginTop: '20px', textAlign: 'center'
  },
  timer: { fontSize: '2.5em', fontFamily: 'monospace', color: '#b08d6d', marginBottom: '25px', fontWeight: 'bold' },
  recordBtn: {
    width: '80px', height: '80px', borderRadius: '50%', border: 'none',
    backgroundColor: '#d32f2f', color: '#fff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
    boxShadow: '0 0 15px rgba(211, 47, 47, 0.4)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  statusText: { marginTop: '20px', color: '#ccc', fontSize: '0.9em', minHeight: '1.2em' },
  recoverBtn: {
    marginTop: '15px', background: '#f57c00', border: 'none', padding: '10px 20px',
    color: '#fff', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9em'
  }
};

// Tiny silent MP4 video (Base64) to trick iOS into keeping the screen on
const SILENT_VIDEO_SOURCE = "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAC5tZGF0AAACrgYF//+1xE0GkQABAAABAAEAAABAAgAAAABHZGF0YQAAAAEAAAAA5W1vb3YAAABsbXZoAAAAAMZ2K+bGdivmAAACWAAAAlgAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAIGdHJhawAAAFx0a2hkAAAAAdZ2K+bWdivmAAAAAQAAAAAAAAlgAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAEAAAABAAAAAAAAAAAAACAAAAAAHmdWR0YQAAAHBtZXRhAAAAAAAAACFoZGlyAAAAAG1kaXJhcHBsAAAAAGlsc3QAAAABhm1kYXQAAAAxZ2JmcmEAAAAtbWRhdAAAAAAAAAAABnJlZWYAAAAHbWFpbgAAAAE1NTU1NTUAAABIdHJhawAAAFx0a2hkAAAAAdZ2K+bWdivmAAAAAgAAAAAAAAlgAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAEAAAABAAAAAAAAAAAAACAAAAAAHmdWR0YQAAAHBtZXRhAAAAAAAAACFoZGlyAAAAAG1kaXJhcHBsAAAAAGlsc3QAAAABhm1kYXQAAAAxZ2JmcmEAAAAtbWRhdAAAAAAAAAAABnJlZWYAAAAHbWFpbgAAAAE1NTU1NTUAAACUbWRpYQAAACBtZGhkAAAAANZ2K+bWdivmAAACWAAAAlgAAAAAVcQAAAAAACJoZGxyAAAAAAAAAAB2aWRlAAAAAAAAAAAAAAAAVmlkZW9IYW5kbGVyAAAAAVxtaW5mAAAAFHZtaGQAAAARAAAAAAAAAAAAAAAKJGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAABMHN0YmwAAACkc3RzZAAAAAAAAAABAAAAhGF2YzEAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAEAAQABkAAABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAALWF2Y0MBAAAALgEBAAH/4QAsZ2QALv/hABhnZAAv/8AAAAf/gAAAAAMBgAAAAAEAAAAAAAAAAAAAAA1zdHRzAAAAAAAAAAEAAAABAAAAAQAAABxzdHNjAAAAAAAAAAEAAAABAAAAAQAAAAEAAAA0c3R6cwAAAAAAAAABAAAAAgAAAAwAAAABAAAAAQAAABxzdGNvAAAAAAAAAAEAAAAMAAAADwAAAAEAAAAUc3RzcwAAAAAAAAABAAAAAQAAABRjdHRzAAAAAAAAAAEAAAABAAAAAQAAAEl1ZHRhAAAAA21ldGEAAAAAAAAAIWhkaXIAAAAAbWRpcmFwcGwAAAAAaWxzdAAAAAGGbWRhdAAAADFnYmZyYQAAAC1tZGF0AAAAAAAAAAAGcmVlZgAAAAdtYWluAAAAATU1NTU1NQ==";

const AudioRecorder = ({ session, onUploadComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [status, setStatus] = useState('Tap REC to start oral journal (Max 60m)');
  const [hasRecovery, setHasRecovery] = useState(false);
  const [mimeType, setMimeType] = useState('audio/webm');

  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null); 
  const videoRef = useRef(null); // Reference to the invisible video

  useEffect(() => {
    async function init() {
        const chunks = await audioDb.getAllChunks();
        if (chunks.length > 0) setHasRecovery(true);

        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) setMimeType('audio/webm;codecs=opus');
        else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) setMimeType('audio/ogg;codecs=opus');
        else if (MediaRecorder.isTypeSupported('audio/mp4')) setMimeType('audio/mp4');
    }
    init();

    return () => {
       releaseWakeLock();
    };
  }, []);

  // --- WAKE LOCK (Standard) ---
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake Lock active (Standard).');
        wakeLockRef.current.addEventListener('release', () => console.log('Wake Lock released.'));
      }
    } catch (err) {
      console.warn('Standard Wake Lock failed, relying on Video Hack.');
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      } catch (err) { console.error(err); }
    }
  };

  // --- WAKE LOCK (iOS Firefox Video Hack) ---
  const startVideoHack = () => {
      if (videoRef.current) {
          videoRef.current.play().catch(e => console.warn("Video Hack blocked:", e));
          console.log("iOS Sleep Prevention (Video) Active");
      }
  };

  const stopVideoHack = () => {
      if (videoRef.current) {
          videoRef.current.pause();
          console.log("iOS Sleep Prevention (Video) Stopped");
      }
  };

  // Handle Tab Switching
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isRecording) {
        await requestWakeLock();
        startVideoHack(); // Restart video if they come back
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRecording]);


  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // 1. ACTIVATE BOTH KEEP-AWAKE METHODS
      await requestWakeLock(); // For Chrome/Android/Safari
      startVideoHack();        // For Firefox iOS

      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32000 });
      mediaRecorderRef.current = recorder;
      
      await audioDb.clearStorage();
      setHasRecovery(false);

      recorder.ondataavailable = async (e) => {
        if (e.data.size > 0) await audioDb.saveChunk(e.data);
      };

      recorder.start(1000);
      setIsRecording(true);
      setStatus('Recording... (Screen kept awake)');
      
      timerRef.current = setInterval(() => setDuration(prev => prev + 1), 1000);
    } catch (err) {
      console.error("Mic Error:", err);
      setStatus('Error: Microphone access denied. Check browser settings.');
    }
  };

  const stopRecording = async () => {
    setIsRecording(false);
    clearInterval(timerRef.current);
    
    // 2. RELEASE BOTH LOCKS
    await releaseWakeLock();
    stopVideoHack();

    if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    setStatus('Finalizing Audio...');
    setTimeout(async () => { await handleUpload(); }, 1500);
  };

  const handleUpload = async () => {
    setIsUploading(true);
    setStatus('Reassembling file...');
    
    const blob = await audioDb.recoverBlob(mimeType);
    if (!blob || blob.size === 0) {
        setStatus('Error: No audio data found.');
        setIsUploading(false);
        return;
    }

    setStatus('Uploading... (Do not close tab)');
    
    try {
        const fileExt = mimeType.includes('mp4') ? 'mp4' : (mimeType.includes('ogg') ? 'ogg' : 'webm');
        const uploadFile = new File([blob], `hhy_5dd_${Date.now()}.${fileExt}`, { type: mimeType });
        const uploadResult = await storage.upload('journal_audio', uploadFile, { product: 'hhy' });
        const audioPath = uploadResult.path;

        setStatus('Saving Entry...');
        const entryData = await db.journal.create({
            audio_path: audioPath,
            transcription: null,
            content: '(Processing Audio...)',
            prompt_key: 'oral_journal',
        });

        setStatus('Transcribing...');
        functions.transcribeAudio({
            filePath: audioPath,
            userId: session.user.id,
            fileType: mimeType,
            entryId: entryData.id
        }).catch(err => console.warn("Auto-transcription trigger warning:", err));

        setStatus('Saved!');
        await audioDb.clearStorage();
        setDuration(0);
        if (onUploadComplete) onUploadComplete();

    } catch (error) {
        console.error("Upload process failed:", error);
        setStatus(`Error: ${error.message}`);
    } finally {
        setIsUploading(false);
    }
  };

  const handleRecover = async () => { setStatus('Attempting recovery...'); await handleUpload(); };

  // Format MM:SS for display
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  return (
    <div style={styles.container}>
      {/* --- INVISIBLE VIDEO FOR iOS FIREFOX --- */}
      <video 
        ref={videoRef}
        playsInline 
        muted 
        loop 
        src={SILENT_VIDEO_SOURCE}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0.01, pointerEvents: 'none' }}
      />
      {/* --------------------------------------- */}

      <div style={styles.timer}>{formatTime(duration)}</div>
      {!isRecording ? (
          <button style={styles.recordBtn} onClick={startRecording} disabled={isUploading}>
            {isUploading ? 'WAIT' : 'REC'}
          </button>
      ) : (
          <button style={{...styles.recordBtn, backgroundColor: '#333', animation: 'pulse 1.5s infinite'}} onClick={stopRecording}>STOP</button>
      )}
      <div style={styles.statusText}>{status}</div>
      {!isRecording && !isUploading && hasRecovery && (
          <div style={{marginTop: '20px', borderTop:'1px solid #555', paddingTop:'15px', width:'100%'}}>
              <p style={{color: '#f57c00', fontSize:'0.9em', margin:'0 0 10px 0'}}>⚠️ Unsaved audio found.</p>
              <button style={styles.recoverBtn} onClick={handleRecover}>Recover & Upload</button>
          </div>
      )}
      <style>{`@keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.7); } 70% { box-shadow: 0 0 0 15px rgba(211, 47, 47, 0); } 100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0); } }`}</style>
    </div>
  );
};

export default AudioRecorder;